/**
 * Échappement HTML et normalisation d'URL externe.
 *
 * Durci en 4.66.3 (audit run-1, findings 6 et 7). Trois implémentations
 * d'`escapeHtml` coexistaient côté client, dont deux fausses :
 *
 * - `app/pages/owner/promote.vue` portait la table complète et correcte — c'est
 *   elle qui est extraite ici, à l'identique.
 * - `app/components/holi/SearchMapV3.vue` et `DepartmentMap.vue` passaient par
 *   `document.createElement('div')` + `textContent` / `innerHTML`. Ce détour
 *   n'échappe **jamais** le guillemet double : le navigateur ne le sérialise pas
 *   en entité dans du contenu textuel, parce qu'il y est inoffensif. Or le
 *   résultat était interpolé en `alt="${name}"` et `src="${photoUrl}"` dans les
 *   popups Leaflet — un nom d'établissement contenant `" onload="` y fermait
 *   l'attribut et en ouvrait un vrai. Le contenu est saisi par le propriétaire
 *   et rendu sur des pages publiques : XSS stocké, déclenché au clic sur un pin.
 *
 * L'ordre des remplacements n'est pas cosmétique : `&` doit passer en PREMIER,
 * sinon les entités produites par les remplacements suivants sont ré-échappées
 * et `Bed & Breakfast` sortirait en `Bed &amp;amp; Breakfast`.
 *
 * Aucune API DOM ici, volontairement : `safeExternalUrl` est appelée en SSR sur
 * les fiches publiques, où `document` n'existe pas. C'est aussi ce qui rend le
 * module testable sans environnement navigateur.
 *
 * Le paramètre est typé `unknown` et non `string` : les appelants passent des
 * champs d'API (`point.publicNumber`, `est.name`) dont le type n'est garanti
 * qu'à la compilation. La version DOM remplacée tolérait une valeur absente
 * (`div.textContent = undefined` rendait `"undefined"`) ; un `.replace` direct
 * aurait transformé un payload dégradé en popup mort au clic. On coerce donc,
 * et une valeur nulle donne la chaîne vide plutôt que le mot « undefined ».
 *
 * NOTE — ce module n'est PAS l'unique table d'échappement du dépôt : il en
 * subsiste quatre côté serveur (`server/utils/email.ts`, `email-layout.ts`,
 * `server/api/feedback.post.ts`, plus une copie dans `email.spec.ts`), non
 * migrées par ce lot. Leur consolidation est tracée en travail différé.
 */
export function escapeHtml(value: unknown): string {
  if (value == null) return ''

  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Filtre une URL externe destinée à un `href` / `to` — défense au RENDU.
 *
 * FILTRE, et non normalisateur : la chaîne d'entrée est retournée telle quelle
 * quand elle est acceptée, jamais le `href` reconstruit par le parseur. C'est
 * délibéré — réécrire l'URL changerait le rendu de fiches parfaitement saines,
 * ce que ce lot s'interdit. N'attendez donc de cette fonction aucune
 * normalisation de casse, d'espaces de tête ou de barre oblique finale.
 *
 * `.url()` de Zod accepte n'importe quel schéma d'URI, `javascript:` et `data:`
 * compris. Le garde-fou de protocole a bien été ajouté aux schémas
 * `establishment` et `gite` en 4.56.0, mais une validation à l'écriture ne
 * nettoie pas ce qui est déjà en base : tout `websiteUrl` enregistré AVANT ce
 * durcissement repart verbatim dans un `:href`. D'où cette seconde barrière, au
 * point de rendu, qui couvre le passif sans dépendre d'une migration de données.
 *
 * Une URL au protocole non autorisé est traitée comme ABSENTE (retour `null`),
 * jamais rendue en texte : afficher `javascript:alert(1)` à côté d'un libellé
 * « site web » inviterait le visiteur à le recopier. `new URL` jette sur une
 * entrée illisible, ce qui est le même verdict.
 */
export function safeExternalUrl(url: string | null | undefined): string | null {
  if (!url) return null

  try {
    const { protocol } = new URL(url)
    return protocol === 'http:' || protocol === 'https:' ? url : null
  }
  catch {
    return null
  }
}
